// src/features/invoices/components/RevenueAnalyticsDashboard.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { TrendingUp, TrendingDown, Users, FileText, BarChart3, ArrowLeft, ArrowRight, ChevronDown, UserCheck, Inbox, Calculator, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../auth';

// ============================================
// TYPES
// ============================================

interface RawInvoice {
  id: string;
  year: number;
  month: number;
  netto_betrag: number;
  ist_storno: boolean;
  empfaenger_firma: string;
  empfaenger_kundennummer: string | null;
  customer_id: string | null;
  line_items: { beschreibung: string; menge: number }[];
}

interface MonthData {
  year: number;
  month: number;
  key: string; // "2025-03"
  netto: number;
  abrechnungen: number;
  kunden: Set<string>;
}

interface CustomerData {
  name: string;
  kdnr: string;
  netto: number;
  abrechnungen: number;
  monate: number;
}

interface CustomerGroupInfo {
  id: string;
  name: string;
}

interface GroupedTopItem {
  name: string;
  value: number;
  secondary?: string;
  groupId?: string;
  children?: { name: string; value: number; secondary?: string }[];
}

interface StaffInfo {
  id: string;
  full_name: string;
  initials: string;
  is_active: boolean;
  in_unit_economics: boolean;
  ist_support_staff: boolean;
}

interface StaffPerformanceRow {
  staffId: string;
  full_name: string;
  initials: string;
  netto: number;
  abrechnungen: number;
  kunden: number;
}

interface StaffMonthlyCell {
  month: number;
  netto: number;
  abrechnungen: number;
}

interface StaffMonthlyRow {
  staffId: string;
  full_name: string;
  initials: string;
  cells: StaffMonthlyCell[];
}

interface MonthlyStatusEntry {
  customer_id: string;
  year: number;
  month: number;
  finalized_by: string;
}

interface PersonalkostenEntry {
  staff_id: string;
  year: number;
  month: number;
  ag_kosten_gesamt: number;
}

/** Ein Block einer erfassten DATEV-Rechnung (Tabelle datev_kostenbloecke) */
interface DatevKostenblockEntry {
  customer_id: string | null;
  kategorie: 'mandant' | 'allgemein' | 'nicht_zuordenbar';
  kosten_ohne_bonus: number;
  bonus_anteil: number;
  year: number;
  month: number;
}

interface UnitEconomicsStaffRow {
  staffId: string;
  full_name: string;
  initials: string;
  istSupportStaff: boolean;
  abrechnungen: number;
  netto: number;
  personalkosten: number | null;
  overheadAnteil: number;
  datev: number;
  datevAllgemeinAnteil: number;
}

// ============================================
// CONSTANTS
// ============================================

const GERMAN_MONTHS: Record<number, string> = {
  1: 'Jan', 2: 'Feb', 3: 'Mär', 4: 'Apr', 5: 'Mai', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Okt', 11: 'Nov', 12: 'Dez'
};

const GERMAN_MONTHS_FULL: Record<number, string> = {
  1: 'Januar', 2: 'Februar', 3: 'März', 4: 'April', 5: 'Mai', 6: 'Juni',
  7: 'Juli', 8: 'August', 9: 'September', 10: 'Oktober', 11: 'November', 12: 'Dezember'
};

const formatEur = (amount: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const formatEurExact = (amount: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

const formatPct = (value: number): string =>
  `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;

const formatNum = (value: number): string =>
  new Intl.NumberFormat('de-DE').format(value);

// Kombinierte Delta-Darstellung: absoluter Wert + Prozent in einer Zelle
const formatEurDelta = (delta: number, pct: number | null): string => {
  const eurStr = `${delta >= 0 ? '+' : ''}${formatEurExact(delta)}`;
  const pctStr = pct !== null ? ` (${formatPct(pct)})` : '';
  return `${eurStr}${pctStr}`;
};

const formatNumDelta = (delta: number, pct: number | null): string => {
  const numStr = `${delta >= 0 ? '+' : ''}${formatNum(delta)}`;
  const pctStr = pct !== null ? ` (${formatPct(pct)})` : '';
  return `${numStr}${pctStr}`;
};

/** Anteil am Umsatz, ohne Vorzeichen */
const formatQuote = (teil: number, ganzes: number): string =>
  ganzes !== 0 ? `${((teil / ganzes) * 100).toFixed(1)}%` : '–';

/** Monatsliste als lesbare Aufzählung */
const formatMonatsliste = (monate: number[]): string =>
  monate.map(m => GERMAN_MONTHS_FULL[m]).join(', ');

// ============================================
// DATA LOADING
// ============================================

const loadAllInvoiceData = async (): Promise<RawInvoice[]> => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      id, year, month, netto_betrag, ist_storno,
      empfaenger_firma, empfaenger_kundennummer, customer_id,
      line_items:invoice_line_items(beschreibung, menge)
    `)
    .in('status', ['importiert', 'freigegeben', 'versendet'])
    .limit(100000);

  if (error) {
    console.error('[RevenueAnalytics] Fehler beim Laden:', error);
    return [];
  }
  return (data || []) as RawInvoice[];
};

// Rechnungsentwürfe werden bewusst getrennt geladen. Sie speisen ausschließlich
// die Umsatzprognose der Monats-Vergleich-Tabelle und dürfen nicht in rawData
// landen, weil daraus KPIs, Charts, Jahresvergleich, Top-Listen,
// Mitarbeiter-Performance und Unit Economics berechnet werden.
const loadDraftInvoiceData = async (): Promise<RawInvoice[]> => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      id, year, month, netto_betrag, ist_storno,
      empfaenger_firma, empfaenger_kundennummer, customer_id,
      line_items:invoice_line_items(beschreibung, menge)
    `)
    .eq('status', 'entwurf')
    .limit(100000);

  if (error) {
    console.error('[RevenueAnalytics] Fehler beim Laden der Entwürfe:', error);
    return [];
  }
  return (data || []) as RawInvoice[];
};

// DATEV-Kosten aus den über die DATEV-Weiterberechnung erfassten Rechnungen.
// Periode ist der Monat der DATEV-Rechnung (Entscheidung 06.10.2026).
const loadDatevKostenData = async (): Promise<DatevKostenblockEntry[]> => {
  const { data, error } = await supabase
    .from('datev_kostenbloecke')
    .select('customer_id, kategorie, kosten_ohne_bonus, bonus_anteil, rechnung:datev_kostenrechnungen!inner(year, month)')
    .gte('rechnung.year', 2026)
    .limit(100000);

  if (error) {
    console.error('[RevenueAnalytics] Fehler beim Laden der DATEV-Kosten:', error);
    return [];
  }

  const rows = (data || []) as unknown as Array<{
    customer_id: string | null;
    kategorie: DatevKostenblockEntry['kategorie'];
    kosten_ohne_bonus: number | string;
    bonus_anteil: number | string;
    rechnung: { year: number; month: number } | { year: number; month: number }[] | null;
  }>;

  const result: DatevKostenblockEntry[] = [];
  for (const r of rows) {
    const rechnung = Array.isArray(r.rechnung) ? r.rechnung[0] : r.rechnung;
    if (!rechnung) continue;
    result.push({
      customer_id: r.customer_id,
      kategorie: r.kategorie,
      kosten_ohne_bonus: Number(r.kosten_ohne_bonus),
      bonus_anteil: Number(r.bonus_anteil),
      year: rechnung.year,
      month: rechnung.month
    });
  }
  return result;
};

// ============================================
// DATA PROCESSING
// ============================================

const isLohnPosition = (beschreibung: string): boolean => {
  const bl = beschreibung.toLowerCase();
  return (bl.includes('lohn') && bl.includes('abrechnung')) || bl.includes('lohana');
};

const processData = (raw: RawInvoice[]) => {
  const monthMap = new Map<string, MonthData>();
  const customerMap = new Map<string, CustomerData>();
  const customerMonths = new Map<string, Set<string>>();

  for (const inv of raw) {
    const key = `${inv.year}-${String(inv.month).padStart(2, '0')}`;
    const kundenKey = inv.empfaenger_kundennummer || inv.empfaenger_firma;

    // Monthly aggregation
    if (!monthMap.has(key)) {
      monthMap.set(key, { year: inv.year, month: inv.month, key, netto: 0, abrechnungen: 0, kunden: new Set() });
    }
    const md = monthMap.get(key)!;
    md.netto += Number(inv.netto_betrag);

    if (!inv.ist_storno && kundenKey) {
      md.kunden.add(kundenKey);
    }

    // Lohnabrechnungen count from line items
    for (const li of (inv.line_items || [])) {
      if (isLohnPosition(li.beschreibung)) {
        const menge = inv.ist_storno ? -Number(li.menge) : Number(li.menge);
        md.abrechnungen += menge;
      }
    }

    // Customer aggregation (only non-storno)
    if (!inv.ist_storno && kundenKey) {
      if (!customerMap.has(kundenKey)) {
        customerMap.set(kundenKey, { name: inv.empfaenger_firma, kdnr: inv.empfaenger_kundennummer || '', netto: 0, abrechnungen: 0, monate: 0 });
      }
      const cd = customerMap.get(kundenKey)!;
      cd.netto += Number(inv.netto_betrag);

      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          cd.abrechnungen += Number(li.menge);
        }
      }

      if (!customerMonths.has(kundenKey)) customerMonths.set(kundenKey, new Set());
      customerMonths.get(kundenKey)!.add(key);
    }
  }

  // Set monate count
  customerMonths.forEach((monthSet, kundenKey) => {
    const cd = customerMap.get(kundenKey);
    if (cd) cd.monate = monthSet.size;
  });

  const months = Array.from(monthMap.values()).sort((a, b) => a.key.localeCompare(b.key));
  const customers = Array.from(customerMap.values());

  return { months, customers };
};

// ============================================
// SUB-COMPONENTS
// ============================================

const KpiCard: React.FC<{
  label: string;
  value: string;
  change?: number;
  subtext?: string;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, change, subtext, icon, color }) => (
  <div style={{
    backgroundColor: 'white', borderRadius: '12px', padding: '20px',
    border: '1px solid #E5E7EB', flex: '1', minWidth: '180px'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
      <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>{label}</span>
      <div style={{
        width: '36px', height: '36px', borderRadius: '8px',
        backgroundColor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </div>
    </div>
    <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>{value}</div>
    {change !== undefined && (
      <div style={{
        fontSize: '13px', fontWeight: '500',
        color: change >= 0 ? '#059669' : '#DC2626',
        display: 'flex', alignItems: 'center', gap: '4px'
      }}>
        {change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
        {formatPct(change)} {subtext || 'ggü. Vormonat'}
      </div>
    )}
    {change === undefined && subtext && (
      <div style={{ fontSize: '13px', color: '#6B7280' }}>{subtext}</div>
    )}
  </div>
);

const BarChart: React.FC<{
  data: { label: string; value: number; prevValue?: number }[];
  formatValue: (v: number) => string;
  color: string;
  height?: number;
}> = ({ data, formatValue, color, height = 200 }) => {
  const maxVal = Math.max(...data.map(d => Math.max(d.value, d.prevValue || 0)), 1);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: `${height}px`, padding: '0 4px' }}>
      {data.map((d, i) => {
        const barHeight = Math.max((d.value / maxVal) * (height - 30), 2);
        const prevBarHeight = d.prevValue !== undefined ? Math.max((d.prevValue / maxVal) * (height - 30), 2) : 0;
        return (
          <div key={i} style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: 0 }}>
            <div style={{ fontSize: '10px', color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {formatValue(d.value)}
            </div>
            <div style={{ display: 'flex', gap: '1px', alignItems: 'flex-end', width: '100%', justifyContent: 'center' }}>
              {d.prevValue !== undefined && (
                <div style={{
                  width: '40%', height: `${prevBarHeight}px`, backgroundColor: '#E5E7EB',
                  borderRadius: '3px 3px 0 0', transition: 'height 0.5s ease'
                }} title={`Vorjahr: ${formatValue(d.prevValue)}`} />
              )}
              <div style={{
                width: d.prevValue !== undefined ? '40%' : '60%',
                height: `${barHeight}px`, backgroundColor: color,
                borderRadius: '3px 3px 0 0', transition: 'height 0.5s ease'
              }} title={formatValue(d.value)} />
            </div>
            <div style={{ fontSize: '10px', color: '#9CA3AF', textAlign: 'center' }}>{d.label}</div>
          </div>
        );
      })}
    </div>
  );
};

const GroupedTopList: React.FC<{
  title: string;
  items: GroupedTopItem[];
  formatValue: (v: number) => string;
  color: string;
}> = ({ title, items, formatValue, color }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const maxVal = items.length > 0 ? items[0].value : 1;

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
        <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>{title}</span>
      </div>
      <div>
        {items.map((item, i) => {
          const isGroup = !!item.children && item.children.length > 0;
          const isExpanded = item.groupId ? expandedGroups.has(item.groupId) : false;

          return (
            <React.Fragment key={item.groupId || item.name}>
              <div
                onClick={isGroup ? () => toggleGroup(item.groupId!) : undefined}
                style={{
                  padding: '10px 20px', borderBottom: i < items.length - 1 && !isExpanded ? '1px solid #F3F4F6' : 'none',
                  display: 'flex', alignItems: 'center', gap: '12px',
                  cursor: isGroup ? 'pointer' : 'default',
                  backgroundColor: isGroup ? '#FAFBFC' : 'white'
                }}
              >
                <span style={{
                  width: '24px', height: '24px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: i < 3 ? `${color}15` : '#F3F4F6',
                  color: i < 3 ? color : '#6B7280'
                }}>
                  {i + 1}
                </span>
                <div style={{ flex: '1', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: isGroup ? '600' : '500', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </span>
                    {isGroup && (
                      <span style={{ fontSize: '11px', color: '#9CA3AF', flexShrink: 0 }}>({item.children!.length})</span>
                    )}
                    {isGroup && (
                      <ChevronDown size={14} color="#9CA3AF" style={{
                        flexShrink: 0, transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                      }} />
                    )}
                  </div>
                  <div style={{ height: '4px', borderRadius: '2px', backgroundColor: '#F3F4F6', marginTop: '4px' }}>
                    <div style={{
                      height: '100%', borderRadius: '2px', backgroundColor: color,
                      width: `${(item.value / maxVal) * 100}%`, transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{formatValue(item.value)}</div>
                  {item.secondary && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{item.secondary}</div>}
                </div>
              </div>
              {isGroup && isExpanded && item.children!.map((child, ci) => (
                <div key={ci} style={{
                  padding: '8px 20px 8px 60px', borderBottom: ci < item.children!.length - 1 ? '1px solid #F9FAFB' : '1px solid #F3F4F6',
                  display: 'flex', alignItems: 'center', gap: '12px',
                  backgroundColor: '#FAFBFC'
                }}>
                  <div style={{ flex: '1', minWidth: 0 }}>
                    <span style={{ fontSize: '12px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                      {child.name}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>{formatValue(child.value)}</div>
                    {child.secondary && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{child.secondary}</div>}
                  </div>
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const RevenueAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState<RawInvoice[]>([]);
  const [draftData, setDraftData] = useState<RawInvoice[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMinMonth, setSelectedMinMonth] = useState(1);
  const [selectedMaxMonth, setSelectedMaxMonth] = useState(12);
  const [customerGroups, setCustomerGroups] = useState<Map<string, CustomerGroupInfo>>(new Map());
  const [customerToGroup, setCustomerToGroup] = useState<Map<string, string>>(new Map());
  const [customerNames, setCustomerNames] = useState<Map<string, string>>(new Map());
  const [customerToStaff, setCustomerToStaff] = useState<Map<string, string>>(new Map());
  const [customerIstAuftraggeber, setCustomerIstAuftraggeber] = useState<Map<string, boolean>>(new Map());
  const [customerHatAuftraggeber, setCustomerHatAuftraggeber] = useState<Map<string, boolean>>(new Map());
  const [staffInfo, setStaffInfo] = useState<Map<string, StaffInfo>>(new Map());
  const [monthlyStatuses, setMonthlyStatuses] = useState<MonthlyStatusEntry[]>([]);
  const [personalkosten, setPersonalkosten] = useState<PersonalkostenEntry[]>([]);
  const [datevKosten, setDatevKosten] = useState<DatevKostenblockEntry[]>([]);
  const [staffMetric, setStaffMetric] = useState<'umsatz' | 'abrechnungen'>('umsatz');

  const { user } = useAuth();
  const isTimWolter = user?.email === 'tim.wolter@xtax.de';

  const loadData = useCallback(async () => {
    setLoading(true);
    const [data, drafts, datev] = await Promise.all([
      loadAllInvoiceData(),
      loadDraftInvoiceData(),
      loadDatevKostenData()
    ]);
    setRawData(data);
    setDraftData(drafts);
    setDatevKosten(datev);

    // Lade Kundengruppen, Zuordnungen, Staff, Monats-Finalisierungen und Personalkosten
    const [groupsRes, mappingRes, staffRes, statusRes, kostenRes] = await Promise.all([
      supabase.from('customer_groups').select('id, name').order('name'),
      supabase.from('customers').select('id, company, customer_group_id, zustaendiger_mitarbeiter_id, ist_auftraggeber, auftraggeber_id').eq('deleted', false),
      supabase.from('xtax_staff').select('id, full_name, initials, is_active, in_unit_economics, ist_support_staff'),
      supabase.from('customer_monthly_status')
        .select('customer_id, year, month, finalized_by')
        .not('finalized_by', 'is', null)
        .gte('year', 2026)
        .limit(100000),
      supabase.from('xtax_personalkosten')
        .select('staff_id, year, month, ag_kosten_gesamt')
        .gte('year', 2026)
        .limit(100000)
    ]);

    if (groupsRes.data) {
      const gMap = new Map<string, CustomerGroupInfo>();
      for (const g of groupsRes.data) gMap.set(g.id, { id: g.id, name: g.name });
      setCustomerGroups(gMap);
    }
    if (mappingRes.data) {
      const cMap = new Map<string, string>();
      const nMap = new Map<string, string>();
      const sMap = new Map<string, string>();
      const aMap = new Map<string, boolean>();
      const hMap = new Map<string, boolean>();
      for (const c of mappingRes.data) {
        if (c.customer_group_id) cMap.set(c.id, c.customer_group_id);
        if (c.company) nMap.set(c.id, c.company);
        if (c.zustaendiger_mitarbeiter_id) sMap.set(c.id, c.zustaendiger_mitarbeiter_id);
        aMap.set(c.id, c.ist_auftraggeber === true);
        hMap.set(c.id, !!c.auftraggeber_id);
      }
      setCustomerToGroup(cMap);
      setCustomerNames(nMap);
      setCustomerToStaff(sMap);
      setCustomerIstAuftraggeber(aMap);
      setCustomerHatAuftraggeber(hMap);
    }
    if (staffRes.data) {
      const stMap = new Map<string, StaffInfo>();
      for (const s of staffRes.data) {
        stMap.set(s.id, {
          id: s.id,
          full_name: s.full_name,
          initials: s.initials,
          is_active: s.is_active === true,
          in_unit_economics: s.in_unit_economics === true,
          ist_support_staff: s.ist_support_staff === true
        });
      }
      setStaffInfo(stMap);
    }
    if (statusRes.data) {
      setMonthlyStatuses(statusRes.data as MonthlyStatusEntry[]);
    }
    if (kostenRes.data) {
      setPersonalkosten(kostenRes.data as PersonalkostenEntry[]);
    }

    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const { months } = useMemo(() => processData(rawData), [rawData]);

  // Available years
  const availableYears = useMemo(() => {
    const years = new Set(months.map(m => m.year));
    return Array.from(years).sort();
  }, [months]);

  // Latest month with data per year (for auto-setting the filter)
  const latestMonthForYear = useCallback((year: number): number => {
    const yearMonths = months.filter(m => m.year === year && m.netto !== 0);
    if (yearMonths.length === 0) return 12;
    return Math.max(...yearMonths.map(m => m.month));
  }, [months]);

  // Auto-set Zeitraum when year changes or data loads
  useEffect(() => {
    if (months.length > 0) {
      const latest = latestMonthForYear(selectedYear);
      setSelectedMinMonth(1);
      setSelectedMaxMonth(latest);
    }
  }, [selectedYear, months.length, latestMonthForYear]);

  // Zeitraumgrenzen: es darf nie ein leerer Zeitraum entstehen
  const handleMinMonthChange = (value: number) => {
    setSelectedMinMonth(value);
    if (value > selectedMaxMonth) setSelectedMaxMonth(value);
  };

  const handleMaxMonthChange = (value: number) => {
    setSelectedMaxMonth(value);
    if (value < selectedMinMonth) setSelectedMinMonth(value);
  };

  // Monthly data for selected year + previous year
  const yearData = useMemo(() => {
    const current = months.filter(m => m.year === selectedYear);
    const prev = months.filter(m => m.year === selectedYear - 1);
    const prevMap = new Map(prev.map(m => [m.month, m]));

    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const cur = current.find(m => m.month === month);
      const prv = prevMap.get(month);
      return {
        month,
        label: GERMAN_MONTHS[month],
        netto: cur?.netto || 0,
        abrechnungen: cur?.abrechnungen || 0,
        kunden: cur?.kunden.size || 0,
        nettoVorjahr: prv?.netto || 0,
        abrechnungenVorjahr: prv?.abrechnungen || 0,
      };
    }).filter(d => d.month >= selectedMinMonth && d.month <= selectedMaxMonth && (d.netto !== 0 || d.nettoVorjahr !== 0));
  }, [months, selectedYear, selectedMinMonth, selectedMaxMonth]);

  // YoY table data
  const yoyData = useMemo(() => {
    return yearData.map(d => ({
      month: d.month,
      label: GERMAN_MONTHS_FULL[d.month],
      netto: d.netto,
      nettoVJ: d.nettoVorjahr,
      nettoDelta: d.nettoVorjahr !== 0 ? ((d.netto - d.nettoVorjahr) / Math.abs(d.nettoVorjahr)) * 100 : null,
      abr: d.abrechnungen,
      abrVJ: d.abrechnungenVorjahr,
      abrDelta: d.abrechnungenVorjahr !== 0 ? ((d.abrechnungen - d.abrechnungenVorjahr) / Math.abs(d.abrechnungenVorjahr)) * 100 : null,
      kunden: d.kunden,
    }));
  }, [yearData]);

  // KPIs: based on selected max month
  const kpis = useMemo(() => {
    const latest = months.find(m => m.year === selectedYear && m.month === selectedMaxMonth);
    const prevMonth = selectedMaxMonth > 1
      ? months.find(m => m.year === selectedYear && m.month === selectedMaxMonth - 1)
      : months.find(m => m.year === selectedYear - 1 && m.month === 12);
    const sameMonthLastYear = months.find(m => m.year === selectedYear - 1 && m.month === selectedMaxMonth);

    if (!latest) return null;

    const nettoMoM = prevMonth && prevMonth.netto !== 0 ? ((latest.netto - prevMonth.netto) / Math.abs(prevMonth.netto)) * 100 : 0;
    const abrMoM = prevMonth && prevMonth.abrechnungen !== 0 ? ((latest.abrechnungen - prevMonth.abrechnungen) / Math.abs(prevMonth.abrechnungen)) * 100 : 0;
    const nettoYoY = sameMonthLastYear && sameMonthLastYear.netto !== 0 ? ((latest.netto - sameMonthLastYear.netto) / Math.abs(sameMonthLastYear.netto)) * 100 : null;

    // Kumuliert über den gewählten Zeitraum
    const ytdCurrent = months.filter(m => m.year === selectedYear && m.month >= selectedMinMonth && m.month <= selectedMaxMonth).reduce((s, m) => s + m.netto, 0);
    const ytdPrev = months.filter(m => m.year === selectedYear - 1 && m.month >= selectedMinMonth && m.month <= selectedMaxMonth).reduce((s, m) => s + m.netto, 0);

    return {
      monat: `${GERMAN_MONTHS_FULL[latest.month]} ${latest.year}`,
      netto: latest.netto,
      nettoMoM,
      abrechnungen: latest.abrechnungen,
      abrMoM,
      kunden: latest.kunden.size,
      nettoYoY,
      ytdCurrent,
      ytdPrev,
    };
  }, [months, selectedYear, selectedMinMonth, selectedMaxMonth]);

  // Top customers for selected year (with group aggregation)
  const topCustomersEur = useMemo((): GroupedTopItem[] => {
    const yearInvoices = rawData.filter(inv => inv.year === selectedYear && inv.month >= selectedMinMonth && inv.month <= selectedMaxMonth);
    const map = new Map<string, { name: string; netto: number; abr: number; customerId: string | null }>();
    for (const inv of yearInvoices) {
      const kundenKey = inv.customer_id || inv.empfaenger_kundennummer || inv.empfaenger_firma;
      if (!kundenKey) continue;
      const displayName = (inv.customer_id && customerNames.get(inv.customer_id)) || inv.empfaenger_firma;
      if (!map.has(kundenKey)) map.set(kundenKey, { name: displayName, netto: 0, abr: 0, customerId: inv.customer_id });
      const e = map.get(kundenKey)!;
      e.netto += Number(inv.netto_betrag);
      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          e.abr += inv.ist_storno ? -Number(li.menge) : Number(li.menge);
        }
      }
    }

    // Group by customer_group_id
    const groupAgg = new Map<string, { name: string; netto: number; abr: number; children: { name: string; netto: number; abr: number }[] }>();
    const ungrouped: GroupedTopItem[] = [];

    Array.from(map.values()).filter(c => c.netto > 0).forEach(c => {
      const groupId = c.customerId ? customerToGroup.get(c.customerId) : undefined;
      if (groupId && customerGroups.has(groupId)) {
        if (!groupAgg.has(groupId)) {
          groupAgg.set(groupId, { name: customerGroups.get(groupId)!.name, netto: 0, abr: 0, children: [] });
        }
        const g = groupAgg.get(groupId)!;
        g.netto += c.netto;
        g.abr += c.abr;
        g.children.push({ name: c.name, netto: c.netto, abr: c.abr });
      } else {
        ungrouped.push({ name: c.name, value: c.netto, secondary: `${formatNum(c.abr)} Abrechnungen` });
      }
    });

    const grouped: GroupedTopItem[] = [];
    groupAgg.forEach((g, gId) => {
      g.children.sort((a, b) => b.netto - a.netto);
      grouped.push({
        name: g.name, value: g.netto, secondary: `${formatNum(g.abr)} Abrechnungen`,
        groupId: gId, children: g.children.map(ch => ({ name: ch.name, value: ch.netto, secondary: `${formatNum(ch.abr)} Abr.` }))
      });
    });

    return [...grouped, ...ungrouped].sort((a, b) => b.value - a.value).slice(0, 10);
  }, [rawData, selectedYear, selectedMinMonth, selectedMaxMonth, customerToGroup, customerGroups, customerNames]);

  const topCustomersAbr = useMemo((): GroupedTopItem[] => {
    const yearInvoices = rawData.filter(inv => inv.year === selectedYear && inv.month >= selectedMinMonth && inv.month <= selectedMaxMonth);
    const map = new Map<string, { name: string; netto: number; abr: number; customerId: string | null }>();
    for (const inv of yearInvoices) {
      const kundenKey = inv.customer_id || inv.empfaenger_kundennummer || inv.empfaenger_firma;
      if (!kundenKey) continue;
      const displayName = (inv.customer_id && customerNames.get(inv.customer_id)) || inv.empfaenger_firma;
      if (!map.has(kundenKey)) map.set(kundenKey, { name: displayName, netto: 0, abr: 0, customerId: inv.customer_id });
      const e = map.get(kundenKey)!;
      e.netto += Number(inv.netto_betrag);
      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          e.abr += inv.ist_storno ? -Number(li.menge) : Number(li.menge);
        }
      }
    }

    const groupAgg = new Map<string, { name: string; netto: number; abr: number; children: { name: string; netto: number; abr: number }[] }>();
    const ungrouped: GroupedTopItem[] = [];

    Array.from(map.values()).filter(c => c.abr > 0).forEach(c => {
      const groupId = c.customerId ? customerToGroup.get(c.customerId) : undefined;
      if (groupId && customerGroups.has(groupId)) {
        if (!groupAgg.has(groupId)) {
          groupAgg.set(groupId, { name: customerGroups.get(groupId)!.name, netto: 0, abr: 0, children: [] });
        }
        const g = groupAgg.get(groupId)!;
        g.netto += c.netto;
        g.abr += c.abr;
        g.children.push({ name: c.name, netto: c.netto, abr: c.abr });
      } else {
        ungrouped.push({ name: c.name, value: c.abr, secondary: formatEur(c.netto) });
      }
    });

    const grouped: GroupedTopItem[] = [];
    groupAgg.forEach((g, gId) => {
      g.children.sort((a, b) => b.abr - a.abr);
      grouped.push({
        name: g.name, value: g.abr, secondary: formatEur(g.netto),
        groupId: gId, children: g.children.map(ch => ({ name: ch.name, value: ch.abr, secondary: formatEur(ch.netto) }))
      });
    });

    return [...grouped, ...ungrouped].sort((a, b) => b.value - a.value).slice(0, 10);
  }, [rawData, selectedYear, selectedMinMonth, selectedMaxMonth, customerToGroup, customerGroups, customerNames]);

  // Month vs. previous month comparison (all customers, grouped, expandable)
  const monthComparison = useMemo(() => {
    const curMonth = selectedMaxMonth;
    const curYear = selectedYear;
    const prevMonth = curMonth > 1 ? curMonth - 1 : 12;
    const prevYear = curMonth > 1 ? curYear : curYear - 1;

    type Entry = { name: string; customerId: string | null; abrAkt: number; abrVor: number; nettoAkt: number; nettoVor: number; hasAkt: boolean; abrEnt: number; nettoEnt: number; belastbar: boolean; hatEntwurf: boolean };
    const map = new Map<string, Entry>();

    for (const inv of rawData) {
      const isCur = inv.year === curYear && inv.month === curMonth;
      const isPrev = inv.year === prevYear && inv.month === prevMonth;
      if (!isCur && !isPrev) continue;
      const kundenKey = inv.customer_id || inv.empfaenger_kundennummer || inv.empfaenger_firma;
      if (!kundenKey) continue;
      const displayName = (inv.customer_id && customerNames.get(inv.customer_id)) || inv.empfaenger_firma;
      if (!map.has(kundenKey)) map.set(kundenKey, { name: displayName, customerId: inv.customer_id, abrAkt: 0, abrVor: 0, nettoAkt: 0, nettoVor: 0, hasAkt: false, abrEnt: 0, nettoEnt: 0, belastbar: false, hatEntwurf: false });
      const e = map.get(kundenKey)!;
      const nettoRaw = Number(inv.netto_betrag);
      let abrSigned = 0;
      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          abrSigned += inv.ist_storno ? -Number(li.menge) : Number(li.menge);
        }
      }
      if (isCur) { e.abrAkt += abrSigned; e.nettoAkt += nettoRaw; e.hasAkt = true; }
      else { e.abrVor += abrSigned; e.nettoVor += nettoRaw; }
    }

    // Rechnungsentwürfe des ausgewählten Monats in dieselbe Kundenstruktur
    // einhängen. Ein Entwurf gilt nur dann als belastbar, wenn er mindestens eine
    // Lohnabrechnungs-Position mit einer Menge ungleich null trägt. Entwürfe, die
    // noch leer sind oder ausschließlich weiterberechnete DATEV-Durchlaufkosten
    // enthalten, tragen keinen Betrag; für diese Kunden bleibt es bei der
    // Vormonatsfortschreibung. Die Zeile erscheint trotzdem, damit neue Standorte
    // sichtbar sind, sobald überhaupt ein Entwurf existiert.
    for (const inv of draftData) {
      if (inv.year !== curYear || inv.month !== curMonth) continue;
      // Sammelrechnungsentwürfe der Auftraggeber wachsen erst im Laufe des Monats
      // und bilden den Monatsumsatz nicht ab. Sie werden deshalb gänzlich ignoriert;
      // für Auftraggeber gilt der Ist-Wert, sonst der Vormonatswert.
      if (inv.customer_id && customerIstAuftraggeber.get(inv.customer_id) === true) continue;
      const kundenKey = inv.customer_id || inv.empfaenger_kundennummer || inv.empfaenger_firma;
      if (!kundenKey) continue;
      const displayName = (inv.customer_id && customerNames.get(inv.customer_id)) || inv.empfaenger_firma;
      if (!map.has(kundenKey)) map.set(kundenKey, { name: displayName, customerId: inv.customer_id, abrAkt: 0, abrVor: 0, nettoAkt: 0, nettoVor: 0, hasAkt: false, abrEnt: 0, nettoEnt: 0, belastbar: false, hatEntwurf: false });
      const e = map.get(kundenKey)!;
      e.hatEntwurf = true;
      e.nettoEnt += Number(inv.netto_betrag);
      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          e.abrEnt += inv.ist_storno ? -Number(li.menge) : Number(li.menge);
          if (Number(li.menge) !== 0) e.belastbar = true;
        }
      }
    }

    // Nicht belastbare Entwürfe werden auf null gesetzt, damit sie weder in einer
    // Zelle noch in einer Gruppensumme noch in der Prognose einen Betrag beisteuern.
    map.forEach(e => {
      if (!e.belastbar) { e.abrEnt = 0; e.nettoEnt = 0; }
    });

    type Child = { name: string; abrAkt: number; abrVor: number; nettoAkt: number; nettoVor: number; abrEnt: number; nettoEnt: number; belastbar: boolean; hatIst: boolean };
    type Row = Child & { groupId?: string; children?: Child[] };

    // Sortierung: Kunden mit Ist-Umsatz im gewählten Monat behalten die bisherige
    // Reihenfolge. Kunden ohne Ist-Umsatz werden nach dem Wert sortiert, der für sie
    // in die Prognose eingeht, also nach dem belastbaren Entwurf, sonst nach dem
    // fortgeschriebenen Vormonatsumsatz.
    const prognoseUmsatz = (r: Child): number => (r.belastbar ? r.nettoEnt : r.nettoVor);
    const vergleicheZeilen = (a: Child, b: Child): number => {
      if (a.hatIst !== b.hatIst) return a.hatIst ? -1 : 1;
      if (a.hatIst) return b.abrAkt - a.abrAkt || b.abrVor - a.abrVor;
      return prognoseUmsatz(b) - prognoseUmsatz(a);
    };

    const groupAgg = new Map<string, { name: string; abrAkt: number; abrVor: number; nettoAkt: number; nettoVor: number; abrEnt: number; nettoEnt: number; belastbar: boolean; hatIst: boolean; children: Child[] }>();
    const ungrouped: Row[] = [];

    Array.from(map.values())
      .filter(c => c.abrAkt !== 0 || c.abrVor !== 0 || c.nettoAkt !== 0 || c.nettoVor !== 0 || c.hatEntwurf)
      .forEach(c => {
        const groupId = c.customerId ? customerToGroup.get(c.customerId) : undefined;
        if (groupId && customerGroups.has(groupId)) {
          if (!groupAgg.has(groupId)) groupAgg.set(groupId, { name: customerGroups.get(groupId)!.name, abrAkt: 0, abrVor: 0, nettoAkt: 0, nettoVor: 0, abrEnt: 0, nettoEnt: 0, belastbar: false, hatIst: false, children: [] });
          const g = groupAgg.get(groupId)!;
          g.abrAkt += c.abrAkt; g.abrVor += c.abrVor; g.nettoAkt += c.nettoAkt; g.nettoVor += c.nettoVor;
          g.abrEnt += c.abrEnt; g.nettoEnt += c.nettoEnt;
          g.belastbar = g.belastbar || c.belastbar;
          g.hatIst = g.hatIst || c.hasAkt;
          g.children.push({ name: c.name, abrAkt: c.abrAkt, abrVor: c.abrVor, nettoAkt: c.nettoAkt, nettoVor: c.nettoVor, abrEnt: c.abrEnt, nettoEnt: c.nettoEnt, belastbar: c.belastbar, hatIst: c.hasAkt });
        } else {
          ungrouped.push({ name: c.name, abrAkt: c.abrAkt, abrVor: c.abrVor, nettoAkt: c.nettoAkt, nettoVor: c.nettoVor, abrEnt: c.abrEnt, nettoEnt: c.nettoEnt, belastbar: c.belastbar, hatIst: c.hasAkt });
        }
      });

    const grouped: Row[] = [];
    groupAgg.forEach((g, gId) => {
      g.children.sort(vergleicheZeilen);
      grouped.push({ groupId: gId, name: g.name, abrAkt: g.abrAkt, abrVor: g.abrVor, nettoAkt: g.nettoAkt, nettoVor: g.nettoVor, abrEnt: g.abrEnt, nettoEnt: g.nettoEnt, belastbar: g.belastbar, hatIst: g.hatIst, children: g.children });
    });

    const all: Row[] = [...grouped, ...ungrouped];
    all.sort(vergleicheZeilen);

    // Die Gesamt-Zeile bleibt bewusst reines Ist. Nur so bleibt sie deckungsgleich
    // mit der YoY-Tabelle, den KPI-Karten und den Diagrammen. Entwurfsbeträge
    // fließen ausschließlich in die Prognosezeile.
    const totals = all.reduce((t, r) => ({ abrAkt: t.abrAkt + r.abrAkt, abrVor: t.abrVor + r.abrVor, nettoAkt: t.nettoAkt + r.nettoAkt, nettoVor: t.nettoVor + r.nettoVor }), { abrAkt: 0, abrVor: 0, nettoAkt: 0, nettoVor: 0 });

    const hatEntwuerfe = Array.from(map.values()).some(e => e.belastbar);

    // Umsatzprognose für den laufenden Monat, pro Kunde in dieser Reihenfolge:
    //   1. tatsächlicher Monatswert, sobald im ausgewählten Monat mindestens eine
    //      importierte, freigegebene oder versendete Rechnung vorliegt (hasAkt),
    //   2. sonst der belastbare Rechnungsentwurf des ausgewählten Monats,
    //   3. sonst der Vormonatswert des Kunden.
    let nettoPrognose = 0;
    let abrPrognose = 0;
    map.forEach(e => {
      if (e.hasAkt) {
        nettoPrognose += e.nettoAkt;
        abrPrognose += e.abrAkt;
      } else if (e.belastbar) {
        nettoPrognose += e.nettoEnt;
        abrPrognose += e.abrEnt;
      } else {
        nettoPrognose += e.nettoVor;
        abrPrognose += e.abrVor;
      }
    });

    // Prognose nur anzeigen, wenn der ausgewählte Monat dem abrechnungsrelevanten
    // laufenden Monat entspricht: das ist der Vormonat des Kalendermonats
    // (im Juni wird Mai prognostiziert, ab Anfang Juli dann Juni).
    const now = new Date();
    const refMonth = now.getMonth() + 1 > 1 ? now.getMonth() : 12;
    const refYear = now.getMonth() + 1 > 1 ? now.getFullYear() : now.getFullYear() - 1;
    const isLaufenderMonat = curYear === refYear && curMonth === refMonth;

    return { rows: all, totals, nettoPrognose, abrPrognose, hatEntwuerfe, isLaufenderMonat, curMonth, curYear, prevMonth, prevYear };
  }, [rawData, draftData, selectedYear, selectedMaxMonth, customerToGroup, customerGroups, customerNames, customerIstAuftraggeber]);

  const [expandedCompGroups, setExpandedCompGroups] = useState<Set<string>>(new Set());
  const toggleCompGroup = (groupId: string) => {
    setExpandedCompGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  // Year totals for summary
  const yearTotals = useMemo(() => {
    const currentYearMonths = months.filter(m => m.year === selectedYear && m.month >= selectedMinMonth && m.month <= selectedMaxMonth);
    const prevYearMonths = months.filter(m => m.year === selectedYear - 1 && m.month >= selectedMinMonth && m.month <= selectedMaxMonth);
    return {
      netto: currentYearMonths.reduce((s, m) => s + m.netto, 0),
      nettoPrev: prevYearMonths.reduce((s, m) => s + m.netto, 0),
      abr: currentYearMonths.reduce((s, m) => s + m.abrechnungen, 0),
      abrPrev: prevYearMonths.reduce((s, m) => s + m.abrechnungen, 0),
    };
  }, [months, selectedYear, selectedMinMonth, selectedMaxMonth]);

  // ============================================
  // STAFF PERFORMANCE (Tim only, ab Jahr 2026)
  // Zuordnung pro Invoice:
  //   - customers.ist_auftraggeber = true  -> pauschal Verena Kerkhoff (Sammelrechnungen)
  //   - sonst -> customer_monthly_status.finalized_by für (customer_id, year, month)
  //     kein Eintrag -> Invoice wird nicht gezählt
  // Kein Vorjahresvergleich.
  // ============================================
  const UNASSIGNED_STAFF_ID = '__unassigned__';

  const verenaStaffId = useMemo<string | null>(() => {
    for (const [id, info] of Array.from(staffInfo.entries())) {
      if (info.full_name === 'Verena Kerkhoff') return id;
    }
    return null;
  }, [staffInfo]);

  const staffPerformance = useMemo<StaffPerformanceRow[]>(() => {
    if (!isTimWolter || selectedYear < 2026) return [];

    // Lookup: finalized_by je (customer_id|year|month)
    const finalizationMap = new Map<string, string>();
    for (const st of monthlyStatuses) {
      if (st.year === selectedYear && st.month >= selectedMinMonth && st.month <= selectedMaxMonth && st.finalized_by) {
        finalizationMap.set(`${st.customer_id}|${st.year}|${st.month}`, st.finalized_by);
      }
    }

    const map = new Map<string, {
      netto: number; abrechnungen: number; kunden: Set<string>;
    }>();

    for (const inv of rawData) {
      if (inv.year !== selectedYear || inv.month < selectedMinMonth || inv.month > selectedMaxMonth) continue;
      if (inv.year === 2026 && inv.month < 3) continue; // Performance-Tracking startet März 2026
      if (!inv.customer_id) continue;

      const isAuftraggeber = customerIstAuftraggeber.get(inv.customer_id) === true;

      let staffId: string | null = null;
      if (isAuftraggeber) {
        staffId = verenaStaffId;
      } else {
        staffId = finalizationMap.get(`${inv.customer_id}|${inv.year}|${inv.month}`) || null;
      }

      if (!staffId) continue; // keine Finalisierung und kein Auftraggeber -> nicht zählen

      if (!map.has(staffId)) {
        map.set(staffId, { netto: 0, abrechnungen: 0, kunden: new Set() });
      }
      const s = map.get(staffId)!;

      s.netto += Number(inv.netto_betrag);
      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          s.abrechnungen += inv.ist_storno ? -Number(li.menge) : Number(li.menge);
        }
      }
      if (!inv.ist_storno) {
        s.kunden.add(inv.customer_id);
      }
    }

    const rows: StaffPerformanceRow[] = [];
    map.forEach((data, staffId) => {
      if (staffId === UNASSIGNED_STAFF_ID) {
        rows.push({
          staffId,
          full_name: 'Nicht zugeordnet',
          initials: '?',
          netto: data.netto,
          abrechnungen: data.abrechnungen,
          kunden: data.kunden.size,
        });
      } else {
        const info = staffInfo.get(staffId);
        rows.push({
          staffId,
          full_name: info?.full_name || 'Unbekannt',
          initials: info?.initials || '?',
          netto: data.netto,
          abrechnungen: data.abrechnungen,
          kunden: data.kunden.size,
        });
      }
    });

    return rows
      .filter(r => r.netto !== 0 || r.abrechnungen !== 0)
      .sort((a, b) => {
        const aVal = staffMetric === 'umsatz' ? a.netto : a.abrechnungen;
        const bVal = staffMetric === 'umsatz' ? b.netto : b.abrechnungen;
        return bVal - aVal;
      });
  }, [isTimWolter, selectedYear, selectedMinMonth, selectedMaxMonth, rawData, monthlyStatuses, customerIstAuftraggeber, verenaStaffId, staffInfo, staffMetric]);

  // Heatmap: pro Mitarbeiter monatliche Werte des gewählten Zeitraums (gleiche Zuordnungslogik)
  const staffMonthlyHeatmap = useMemo<{ rows: StaffMonthlyRow[]; maxValue: number }>(() => {
    if (!isTimWolter || selectedYear < 2026 || staffPerformance.length === 0) return { rows: [], maxValue: 0 };

    // Gleiche Finalisierungs-Lookup wie in staffPerformance
    const finalizationMap = new Map<string, string>();
    for (const st of monthlyStatuses) {
      if (st.year === selectedYear && st.finalized_by) {
        finalizationMap.set(`${st.customer_id}|${st.year}|${st.month}`, st.finalized_by);
      }
    }

    const map = new Map<string, Map<number, { netto: number; abrechnungen: number }>>();

    for (const inv of rawData) {
      if (inv.year !== selectedYear || inv.month < selectedMinMonth || inv.month > selectedMaxMonth) continue;
      if (inv.year === 2026 && inv.month < 3) continue; // Performance-Tracking startet März 2026
      if (!inv.customer_id) continue;

      const isAuftraggeber = customerIstAuftraggeber.get(inv.customer_id) === true;
      let staffId: string | null = null;
      if (isAuftraggeber) {
        staffId = verenaStaffId;
      } else {
        staffId = finalizationMap.get(`${inv.customer_id}|${inv.year}|${inv.month}`) || null;
      }

      if (!staffId) continue;

      if (!map.has(staffId)) map.set(staffId, new Map());
      const sMonths = map.get(staffId)!;
      if (!sMonths.has(inv.month)) sMonths.set(inv.month, { netto: 0, abrechnungen: 0 });
      const monthData = sMonths.get(inv.month)!;

      monthData.netto += Number(inv.netto_betrag);
      for (const li of (inv.line_items || [])) {
        if (isLohnPosition(li.beschreibung)) {
          monthData.abrechnungen += inv.ist_storno ? -Number(li.menge) : Number(li.menge);
        }
      }
    }

    const monate: number[] = [];
    for (let m = selectedMinMonth; m <= selectedMaxMonth; m++) monate.push(m);

    const rows: StaffMonthlyRow[] = staffPerformance.map(perf => {
      const monthMap = map.get(perf.staffId) || new Map();
      const cells: StaffMonthlyCell[] = monate.map(month => {
        const data = monthMap.get(month);
        return {
          month,
          netto: data?.netto || 0,
          abrechnungen: data?.abrechnungen || 0,
        };
      });
      return {
        staffId: perf.staffId,
        full_name: perf.full_name,
        initials: perf.initials,
        cells,
      };
    });

    let maxValue = 0;
    rows.forEach(r => {
      r.cells.forEach(c => {
        const val = staffMetric === 'umsatz' ? c.netto : c.abrechnungen;
        if (val > maxValue) maxValue = val;
      });
    });

    return { rows, maxValue };
  }, [isTimWolter, selectedYear, selectedMinMonth, selectedMaxMonth, rawData, monthlyStatuses, customerIstAuftraggeber, verenaStaffId, staffPerformance, staffMetric]);

  // Maximalwert für die Bar-Indikatoren in der Mitarbeiter-Tabelle
  const staffMaxBarValue = useMemo(() => {
    if (staffPerformance.length === 0) return 1;
    const values = staffPerformance.map(r => staffMetric === 'umsatz' ? r.netto : r.abrechnungen);
    return Math.max(...values, 1);
  }, [staffPerformance, staffMetric]);

  // ============================================
  // UNIT ECONOMICS (Tim only, ab Jahr 2026)
  //
  // Umsatz und Abrechnungen stammen unverändert aus derselben Zuordnung wie
  // die Mitarbeiter-Performance darüber (staffMonthlyHeatmap).
  //
  // Als Berechnungsbasis dienen ausschließlich Monate im gewählten Zeitraum,
  // für die in xtax_personalkosten überhaupt Gehaltsdaten vorliegen UND für
  // die eine DATEV-Rechnung erfasst ist. Liegen für einen Monat Gehaltsdaten
  // vor, gelten sie als vollständig. Monate ohne Gehaltsdaten oder ohne
  // DATEV-Rechnung werden als Lücke ausgewiesen und fließen nicht in die
  // Berechnung ein.
  //
  // Support Staff wird gleichmäßig auf die abrechnenden Mitarbeiter mit
  // erfassten Kosten umgelegt.
  //
  // DATEV-Kosten (Entscheidung 06.10.2026), Monat = Monat der DATEV-Rechnung:
  //   - Mandantenblöcke inklusive Bonusanteil gehen an den Kunden und von dort
  //     an die Mitarbeiterin: Untermandanten eines Auftraggebers an Verena
  //     Kerkhoff (wie die Sammelrechnung), sonst finalized_by des Kunden im
  //     Monat, ersatzweise der zuständige Mitarbeiter. Ohne Mitarbeiter laufen
  //     die Kosten unter DATEV allgemein.
  //   - DATEV allgemein (beraterbezogene Leistungen, Mandant 10000, nicht
  //     zuordenbare Blöcke) wird wie der Support Staff gleichmäßig umgelegt.
  // ============================================
  const unitEconomics = useMemo(() => {
    if (!isTimWolter || selectedYear < 2026) return null;

    const startMonat = Math.max(selectedMinMonth, selectedYear === 2026 ? 3 : 1);

    // Belegung je Monat
    const besetzungJeMonat = new Map<number, Set<string>>();
    for (const k of personalkosten) {
      if (k.year !== selectedYear) continue;
      if (k.month < startMonat || k.month > selectedMaxMonth) continue;
      if (!besetzungJeMonat.has(k.month)) besetzungJeMonat.set(k.month, new Set());
      besetzungJeMonat.get(k.month)!.add(k.staff_id);
    }

    // Monate mit erfasster DATEV-Rechnung
    const datevMonate = new Set<number>();
    for (const b of datevKosten) {
      if (b.year === selectedYear) datevMonate.add(b.month);
    }

    const vollstaendigeMonate: number[] = [];
    const offeneMonate: number[] = [];
    const offeneMonatePersonal: number[] = [];
    const offeneMonateDatev: number[] = [];
    for (let m = startMonat; m <= selectedMaxMonth; m++) {
      const besetzt = besetzungJeMonat.get(m);
      const personalVorhanden = !!besetzt && besetzt.size > 0;
      const datevVorhanden = datevMonate.has(m);
      if (personalVorhanden && datevVorhanden) {
        vollstaendigeMonate.push(m);
      } else {
        offeneMonate.push(m);
        if (!personalVorhanden) offeneMonatePersonal.push(m);
        if (!datevVorhanden) offeneMonateDatev.push(m);
      }
    }
    const vollSet = new Set(vollstaendigeMonate);

    // Umsatz und Abrechnungen über den gesamten gewählten Zeitraum
    let umsatzFenster = 0;
    let abrFenster = 0;
    for (const r of staffMonthlyHeatmap.rows) {
      for (const c of r.cells) {
        if (c.month < startMonat) continue;
        umsatzFenster += c.netto;
        abrFenster += c.abrechnungen;
      }
    }

    // Ansicht ohne Kostenbasis: Umsatz, DATEV und Deckungsbeitrag I über die
    // Monate des Zeitraums, für die eine DATEV-Rechnung erfasst ist
    const fensterDatevMonate: number[] = [];
    for (let m = startMonat; m <= selectedMaxMonth; m++) {
      if (datevMonate.has(m)) fensterDatevMonate.push(m);
    }
    const fensterDatevSet = new Set(fensterDatevMonate);
    let umsatzFensterDatev = 0;
    let abrFensterDatev = 0;
    for (const r of staffMonthlyHeatmap.rows) {
      for (const c of r.cells) {
        if (!fensterDatevSet.has(c.month)) continue;
        umsatzFensterDatev += c.netto;
        abrFensterDatev += c.abrechnungen;
      }
    }
    let datevFenster = 0;
    for (const b of datevKosten) {
      if (b.year !== selectedYear || !fensterDatevSet.has(b.month)) continue;
      datevFenster += b.kosten_ohne_bonus + b.bonus_anteil;
    }

    // Personalkosten über die vollständig erfassten Monate
    let personalAbrechnend = 0;
    let personalSupport = 0;
    const kostenJeStaff = new Map<string, number>();
    for (const k of personalkosten) {
      if (k.year !== selectedYear) continue;
      if (!vollSet.has(k.month)) continue;
      const betrag = Number(k.ag_kosten_gesamt);
      kostenJeStaff.set(k.staff_id, (kostenJeStaff.get(k.staff_id) || 0) + betrag);
      const info = staffInfo.get(k.staff_id);
      if (info && info.ist_support_staff) {
        personalSupport += betrag;
      } else {
        personalAbrechnend += betrag;
      }
    }

    // Umsatz und Abrechnungen über die vollständig erfassten Monate, je Mitarbeiter
    const umsatzJeStaff = new Map<string, { netto: number; abrechnungen: number }>();
    let umsatzBasis = 0;
    let abrBasis = 0;
    for (const r of staffMonthlyHeatmap.rows) {
      let netto = 0;
      let abrechnungen = 0;
      for (const c of r.cells) {
        if (!vollSet.has(c.month)) continue;
        netto += c.netto;
        abrechnungen += c.abrechnungen;
      }
      umsatzJeStaff.set(r.staffId, { netto, abrechnungen });
      umsatzBasis += netto;
      abrBasis += abrechnungen;
    }

    // Kopfzahl für die gleichmäßige Umlage
    let anzahlAbrechnendeMitKosten = 0;
    Array.from(kostenJeStaff.keys()).forEach(id => {
      const info = staffInfo.get(id);
      if (info && !info.ist_support_staff) anzahlAbrechnendeMitKosten++;
    });
    const overheadJeKopf = anzahlAbrechnendeMitKosten > 0 ? personalSupport / anzahlAbrechnendeMitKosten : 0;

    // DATEV-Kosten über die vollständig erfassten Monate
    const finalisierungJeKundeMonat = new Map<string, string>();
    for (const st of monthlyStatuses) {
      if (st.year === selectedYear && st.finalized_by) {
        finalisierungJeKundeMonat.set(`${st.customer_id}|${st.year}|${st.month}`, st.finalized_by);
      }
    }

    const datevJeStaff = new Map<string, number>();
    let datevDirekt = 0;
    let datevAllgemein = 0;
    let datevOhneZuordnung = 0;
    for (const b of datevKosten) {
      if (b.year !== selectedYear || !vollSet.has(b.month)) continue;
      const betrag = b.kosten_ohne_bonus + b.bonus_anteil;

      if (b.kategorie === 'mandant' && b.customer_id) {
        let staffId: string | null = null;
        if (customerHatAuftraggeber.get(b.customer_id) === true) {
          staffId = verenaStaffId;
        } else {
          staffId = finalisierungJeKundeMonat.get(`${b.customer_id}|${b.year}|${b.month}`)
            || customerToStaff.get(b.customer_id)
            || null;
        }
        if (staffId) {
          datevJeStaff.set(staffId, (datevJeStaff.get(staffId) || 0) + betrag);
          datevDirekt += betrag;
          continue;
        }
        datevOhneZuordnung += betrag;
        datevAllgemein += betrag;
        continue;
      }

      if (b.kategorie === 'nicht_zuordenbar') datevOhneZuordnung += betrag;
      datevAllgemein += betrag;
    }
    const datevAllgemeinJeKopf = anzahlAbrechnendeMitKosten > 0 ? datevAllgemein / anzahlAbrechnendeMitKosten : 0;

    // Zeilen je Mitarbeiter, Vereinigung aus Umsatz- und Kostenseite
    const alleIds = new Set<string>();
    Array.from(umsatzJeStaff.keys()).forEach(id => alleIds.add(id));
    Array.from(kostenJeStaff.keys()).forEach(id => alleIds.add(id));
    Array.from(datevJeStaff.keys()).forEach(id => alleIds.add(id));

    const zeilen: UnitEconomicsStaffRow[] = [];
    Array.from(alleIds).forEach(id => {
      const info = staffInfo.get(id);
      const heatmapRow = staffMonthlyHeatmap.rows.find(r => r.staffId === id);
      const umsatzEintrag = umsatzJeStaff.get(id) || { netto: 0, abrechnungen: 0 };
      const istSupport = info ? info.ist_support_staff : false;
      const eigeneKosten = kostenJeStaff.has(id) ? kostenJeStaff.get(id)! : null;
      const erhaeltUmlage = !istSupport && eigeneKosten !== null;
      zeilen.push({
        staffId: id,
        full_name: info ? info.full_name : (heatmapRow ? heatmapRow.full_name : 'Unbekannt'),
        initials: info ? info.initials : (heatmapRow ? heatmapRow.initials : '?'),
        istSupportStaff: istSupport,
        abrechnungen: umsatzEintrag.abrechnungen,
        netto: umsatzEintrag.netto,
        personalkosten: istSupport ? null : eigeneKosten,
        overheadAnteil: erhaeltUmlage ? overheadJeKopf : 0,
        datev: datevJeStaff.get(id) || 0,
        datevAllgemeinAnteil: erhaeltUmlage ? datevAllgemeinJeKopf : 0
      });
    });
    zeilen.sort((a, b) => b.netto - a.netto);

    const datevBasis = datevDirekt;
    const dbI = umsatzBasis - datevBasis;
    const dbII = dbI - personalAbrechnend;
    const dbIII = dbII - personalSupport - datevAllgemein;

    const produktionskostenJeAbr = abrBasis > 0 ? (datevBasis + personalAbrechnend) / abrBasis : 0;
    const vollkostenJeAbr = abrBasis > 0 ? (datevBasis + personalAbrechnend + personalSupport + datevAllgemein) / abrBasis : 0;

    return {
      hatKostenBasis: vollstaendigeMonate.length > 0 && abrBasis > 0,
      vollstaendigeMonate,
      offeneMonate,
      offeneMonatePersonal,
      offeneMonateDatev,
      umsatzFenster,
      abrFenster,
      fensterDatevMonate,
      umsatzFensterDatev,
      abrFensterDatev,
      datevFenster,
      umsatzBasis,
      abrBasis,
      datevBasis,
      datevAllgemein,
      datevAllgemeinJeKopf,
      datevOhneZuordnung,
      dbI,
      dbII,
      dbIII,
      personalAbrechnend,
      personalSupport,
      overheadJeKopf,
      anzahlAbrechnendeMitKosten,
      produktionskostenJeAbr,
      vollkostenJeAbr,
      zeilen
    };
  }, [isTimWolter, selectedYear, selectedMinMonth, selectedMaxMonth, staffInfo, personalkosten, staffMonthlyHeatmap, datevKosten, monthlyStatuses, customerHatAuftraggeber, customerToStaff, verenaStaffId]);

  // Hilfsvariable, damit wir customerToStaff nicht als "unused" markieren (bleibt für
  // zukünftige Nutzung und zur Vollständigkeit der Customer-Stammdaten erhalten).
  void customerToStaff;

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
        <BarChart3 size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: '16px', fontWeight: '500' }}>Umsatzdaten werden geladen...</div>
      </div>
    );
  }

  // Beschriftung des gewählten Zeitraums
  const istGanzesJahr = selectedMinMonth === 1 && selectedMaxMonth === 12;
  const zeitraumKurz = selectedMinMonth === selectedMaxMonth
    ? GERMAN_MONTHS[selectedMinMonth]
    : `${GERMAN_MONTHS[selectedMinMonth]}–${GERMAN_MONTHS[selectedMaxMonth]}`;
  const zeitraumPrefix = istGanzesJahr ? '' : `${zeitraumKurz} `;

  const monatsSpalten: number[] = [];
  for (let m = selectedMinMonth; m <= selectedMaxMonth; m++) monatsSpalten.push(m);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>
          Umsatzanalyse
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Jahreswähler */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setSelectedYear(y => Math.max(y - 1, availableYears[0] || 2023))}
              disabled={selectedYear <= (availableYears[0] || 2023)}
              style={{
                padding: '6px 10px', backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB',
                borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center'
              }}
            >
              <ArrowLeft size={16} />
            </button>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827', minWidth: '50px', textAlign: 'center' }}>
              {selectedYear}
            </span>
            <button
              onClick={() => setSelectedYear(y => Math.min(y + 1, availableYears[availableYears.length - 1] || 2026))}
              disabled={selectedYear >= (availableYears[availableYears.length - 1] || 2026)}
              style={{
                padding: '6px 10px', backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB',
                borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center'
              }}
            >
              <ArrowRight size={16} />
            </button>
          </div>

          {/* Von-Monat-Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>von</span>
            <select
              value={selectedMinMonth}
              onChange={e => handleMinMonthChange(Number(e.target.value))}
              style={{
                padding: '6px 28px 6px 10px', fontSize: '14px', fontWeight: '500',
                backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: '6px',
                color: '#111827', cursor: 'pointer', appearance: 'auto'
              }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{GERMAN_MONTHS_FULL[m]}</option>
              ))}
            </select>
          </div>

          {/* Bis-Monat-Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>bis</span>
            <select
              value={selectedMaxMonth}
              onChange={e => handleMaxMonthChange(Number(e.target.value))}
              style={{
                padding: '6px 28px 6px 10px', fontSize: '14px', fontWeight: '500',
                backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: '6px',
                color: '#111827', cursor: 'pointer', appearance: 'auto'
              }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{GERMAN_MONTHS_FULL[m]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {kpis && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <KpiCard
            label="Umsatz (letzter Monat)"
            value={formatEur(kpis.netto)}
            change={kpis.nettoMoM}
            icon={<TrendingUp size={18} color="#3B82F6" />}
            color="#3B82F6"
          />
          <KpiCard
            label="Abrechnungen (letzter Monat)"
            value={formatNum(kpis.abrechnungen)}
            change={kpis.abrMoM}
            icon={<FileText size={18} color="#8B5CF6" />}
            color="#8B5CF6"
          />
          <KpiCard
            label="Aktive Kunden"
            value={String(kpis.kunden)}
            subtext={kpis.monat}
            icon={<Users size={18} color="#059669" />}
            color="#059669"
          />
          <KpiCard
            label={istGanzesJahr ? `YTD ${selectedYear}` : `${zeitraumKurz} ${selectedYear}`}
            value={formatEur(yearTotals.netto)}
            change={yearTotals.nettoPrev !== 0 ? ((yearTotals.netto - yearTotals.nettoPrev) / Math.abs(yearTotals.nettoPrev)) * 100 : undefined}
            subtext={yearTotals.nettoPrev !== 0 ? `ggü. ${selectedYear - 1}` : `${formatNum(yearTotals.abr)} Abrechnungen`}
            icon={<BarChart3 size={18} color="#F59E0B" />}
            color="#F59E0B"
          />
        </div>
      )}

      {/* Charts: Revenue + Abrechnungen */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>Monatlicher Umsatz (netto)</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#9CA3AF' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#3B82F6' }} /> {selectedYear}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#E5E7EB' }} /> {selectedYear - 1}
              </span>
            </div>
          </div>
          <BarChart
            data={yearData.map(d => ({ label: d.label, value: d.netto, prevValue: d.nettoVorjahr }))}
            formatValue={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : formatNum(v)}
            color="#3B82F6"
            height={220}
          />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>Lohnabrechnungen pro Monat</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#9CA3AF' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#8B5CF6' }} /> {selectedYear}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#E5E7EB' }} /> {selectedYear - 1}
              </span>
            </div>
          </div>
          <BarChart
            data={yearData.map(d => ({ label: d.label, value: d.abrechnungen, prevValue: d.abrechnungenVorjahr }))}
            formatValue={v => formatNum(v)}
            color="#8B5CF6"
            height={220}
          />
        </div>
      </div>

      {/* YoY Comparison Table */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: '24px' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
            Year-over-Year Vergleich {selectedYear} vs. {selectedYear - 1}
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Monat</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Umsatz {selectedYear}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Umsatz {selectedYear - 1}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Δ Umsatz</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Abr. {selectedYear}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Abr. {selectedYear - 1}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Δ Abr.</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Kunden</th>
              </tr>
            </thead>
            <tbody>
              {yoyData.map((row, i) => (
                <tr key={row.month} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '10px 16px', fontWeight: '500', color: '#111827' }}>{row.label}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500', color: '#111827' }}>
                    {row.netto !== 0 ? formatEurExact(row.netto) : '–'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                    {row.nettoVJ !== 0 ? formatEurExact(row.nettoVJ) : '–'}
                  </td>
                  <td style={{
                    padding: '10px 16px', textAlign: 'right', fontWeight: '500',
                    color: row.nettoDelta !== null ? (row.nettoDelta >= 0 ? '#059669' : '#DC2626') : '#9CA3AF'
                  }}>
                    {row.nettoDelta !== null ? formatEurDelta(row.netto - row.nettoVJ, row.nettoDelta) : '–'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500', color: '#111827' }}>
                    {row.abr !== 0 ? formatNum(row.abr) : '–'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                    {row.abrVJ !== 0 ? formatNum(row.abrVJ) : '–'}
                  </td>
                  <td style={{
                    padding: '10px 16px', textAlign: 'right', fontWeight: '500',
                    color: row.abrDelta !== null ? (row.abrDelta >= 0 ? '#059669' : '#DC2626') : '#9CA3AF'
                  }}>
                    {row.abrDelta !== null ? formatNumDelta(row.abr - row.abrVJ, row.abrDelta) : '–'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{row.kunden || '–'}</td>
                </tr>
              ))}
              {/* Summenzeile */}
              <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600' }}>
                <td style={{ padding: '10px 16px', color: '#111827' }}>Gesamt</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{formatEurExact(yearTotals.netto)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(yearTotals.nettoPrev)}</td>
                <td style={{
                  padding: '10px 16px', textAlign: 'right',
                  color: yearTotals.nettoPrev !== 0 ? (yearTotals.netto >= yearTotals.nettoPrev ? '#059669' : '#DC2626') : '#9CA3AF'
                }}>
                  {yearTotals.nettoPrev !== 0 ? formatEurDelta(yearTotals.netto - yearTotals.nettoPrev, ((yearTotals.netto - yearTotals.nettoPrev) / Math.abs(yearTotals.nettoPrev)) * 100) : '–'}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{formatNum(yearTotals.abr)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatNum(yearTotals.abrPrev)}</td>
                <td style={{
                  padding: '10px 16px', textAlign: 'right',
                  color: yearTotals.abrPrev !== 0 ? (yearTotals.abr >= yearTotals.abrPrev ? '#059669' : '#DC2626') : '#9CA3AF'
                }}>
                  {yearTotals.abrPrev !== 0 ? formatNumDelta(yearTotals.abr - yearTotals.abrPrev, ((yearTotals.abr - yearTotals.abrPrev) / Math.abs(yearTotals.abrPrev)) * 100) : '–'}
                </td>
                <td style={{ padding: '10px 16px' }} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Customers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <GroupedTopList
          title={`Top 10 Kunden nach Umsatz ${zeitraumPrefix}${selectedYear}`}
          items={topCustomersEur}
          formatValue={formatEur}
          color="#3B82F6"
        />
        <GroupedTopList
          title={`Top 10 Kunden nach Abrechnungen ${zeitraumPrefix}${selectedYear}`}
          items={topCustomersAbr}
          formatValue={formatNum}
          color="#8B5CF6"
        />
      </div>

      {/* Monats-Vergleich: ausgewählter Monat vs. Vormonat */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
            Abrechnungen und Umsatz {GERMAN_MONTHS_FULL[monthComparison.curMonth]} {monthComparison.curYear} vs. {GERMAN_MONTHS_FULL[monthComparison.prevMonth]} {monthComparison.prevYear}
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Kunde</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Abr. {GERMAN_MONTHS[monthComparison.curMonth]}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Abr. {GERMAN_MONTHS[monthComparison.prevMonth]}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Δ Abr.</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Umsatz {GERMAN_MONTHS[monthComparison.curMonth]}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Umsatz {GERMAN_MONTHS[monthComparison.prevMonth]}</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Δ Umsatz</th>
              </tr>
            </thead>
            <tbody>
              {monthComparison.rows.map(row => {
                const isGroup = !!row.children && row.children.length > 0;
                const isExpanded = row.groupId ? expandedCompGroups.has(row.groupId) : false;
                // Ohne Ist-Rechnung im gewählten Monat zeigt die Zeile den belastbaren
                // Entwurf, blau markiert. Reihenfolge identisch zur Prognose.
                const istEntwurf = !row.hatIst && row.belastbar;
                const abrAnzeige = istEntwurf ? row.abrEnt : row.abrAkt;
                const nettoAnzeige = istEntwurf ? row.nettoEnt : row.nettoAkt;
                const aktFarbe = istEntwurf ? '#1D4ED8' : '#111827';
                const abrDelta = row.abrVor !== 0 ? ((abrAnzeige - row.abrVor) / Math.abs(row.abrVor)) * 100 : null;
                const nettoDelta = row.nettoVor !== 0 ? ((nettoAnzeige - row.nettoVor) / Math.abs(row.nettoVor)) * 100 : null;
                return (
                  <React.Fragment key={row.groupId || row.name}>
                    <tr
                      onClick={isGroup ? () => toggleCompGroup(row.groupId!) : undefined}
                      style={{ cursor: isGroup ? 'pointer' : 'default', backgroundColor: isGroup ? '#FAFBFC' : 'white', borderBottom: '1px solid #F3F4F6' }}
                    >
                      <td style={{ padding: '10px 16px', color: '#111827', fontWeight: isGroup ? '600' : '400' }}>
                        {isGroup && <span style={{ display: 'inline-block', width: '14px', color: '#6B7280' }}>{isExpanded ? '▾' : '▸'}</span>}
                        {row.name}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: aktFarbe }}>{abrAnzeige !== 0 ? formatNum(abrAnzeige) : '–'}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{row.abrVor !== 0 ? formatNum(row.abrVor) : '–'}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500', color: abrDelta !== null ? (abrDelta >= 0 ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                        {abrDelta !== null ? formatNumDelta(abrAnzeige - row.abrVor, abrDelta) : '–'}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: aktFarbe }}>{nettoAnzeige !== 0 ? formatEur(nettoAnzeige) : '–'}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{row.nettoVor !== 0 ? formatEur(row.nettoVor) : '–'}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500', color: nettoDelta !== null ? (nettoDelta >= 0 ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                        {nettoDelta !== null ? formatEurDelta(nettoAnzeige - row.nettoVor, nettoDelta) : '–'}
                      </td>
                    </tr>
                    {isGroup && isExpanded && row.children!.map(ch => {
                      const chEntwurf = !ch.hatIst && ch.belastbar;
                      const chAbrAnzeige = chEntwurf ? ch.abrEnt : ch.abrAkt;
                      const chNettoAnzeige = chEntwurf ? ch.nettoEnt : ch.nettoAkt;
                      const chAktFarbe = chEntwurf ? '#1D4ED8' : '#374151';
                      const cAbrDelta = ch.abrVor !== 0 ? ((chAbrAnzeige - ch.abrVor) / Math.abs(ch.abrVor)) * 100 : null;
                      const cNettoDelta = ch.nettoVor !== 0 ? ((chNettoAnzeige - ch.nettoVor) / Math.abs(ch.nettoVor)) * 100 : null;
                      return (
                        <tr key={`${row.groupId}-${ch.name}`} style={{ backgroundColor: '#FCFCFD', borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '8px 16px 8px 40px', color: '#374151', fontSize: '12px' }}>{ch.name}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', color: chAktFarbe, fontSize: '12px' }}>{chAbrAnzeige !== 0 ? formatNum(chAbrAnzeige) : '–'}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', color: '#6B7280', fontSize: '12px' }}>{ch.abrVor !== 0 ? formatNum(ch.abrVor) : '–'}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: '12px', color: cAbrDelta !== null ? (cAbrDelta >= 0 ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                            {cAbrDelta !== null ? formatNumDelta(chAbrAnzeige - ch.abrVor, cAbrDelta) : '–'}
                          </td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', color: chAktFarbe, fontSize: '12px' }}>{chNettoAnzeige !== 0 ? formatEur(chNettoAnzeige) : '–'}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', color: '#6B7280', fontSize: '12px' }}>{ch.nettoVor !== 0 ? formatEur(ch.nettoVor) : '–'}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: '12px', color: cNettoDelta !== null ? (cNettoDelta >= 0 ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                            {cNettoDelta !== null ? formatEurDelta(chNettoAnzeige - ch.nettoVor, cNettoDelta) : '–'}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {/* Summenzeile */}
              <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600' }}>
                <td style={{ padding: '10px 16px', color: '#111827' }}>Gesamt</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{formatNum(monthComparison.totals.abrAkt)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatNum(monthComparison.totals.abrVor)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: monthComparison.totals.abrVor !== 0 ? (monthComparison.totals.abrAkt >= monthComparison.totals.abrVor ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                  {monthComparison.totals.abrVor !== 0 ? formatNumDelta(monthComparison.totals.abrAkt - monthComparison.totals.abrVor, ((monthComparison.totals.abrAkt - monthComparison.totals.abrVor) / Math.abs(monthComparison.totals.abrVor)) * 100) : '–'}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{formatEur(monthComparison.totals.nettoAkt)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatEur(monthComparison.totals.nettoVor)}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: monthComparison.totals.nettoVor !== 0 ? (monthComparison.totals.nettoAkt >= monthComparison.totals.nettoVor ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                  {monthComparison.totals.nettoVor !== 0 ? formatEurDelta(monthComparison.totals.nettoAkt - monthComparison.totals.nettoVor, ((monthComparison.totals.nettoAkt - monthComparison.totals.nettoVor) / Math.abs(monthComparison.totals.nettoVor)) * 100) : '–'}
                </td>
              </tr>
              {/* Prognosezeile: nur im abrechnungsrelevanten laufenden Monat */}
              {monthComparison.isLaufenderMonat && (
                <tr style={{ backgroundColor: '#EFF6FF', fontWeight: '600', borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '10px 16px', color: '#1D4ED8' }}>
                    Prognose {GERMAN_MONTHS_FULL[monthComparison.curMonth]} {monthComparison.curYear}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#1D4ED8' }}>
                    {formatNum(monthComparison.abrPrognose)}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                    {formatNum(monthComparison.totals.abrVor)}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: monthComparison.totals.abrVor !== 0 ? (monthComparison.abrPrognose >= monthComparison.totals.abrVor ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                    {monthComparison.totals.abrVor !== 0 ? formatNumDelta(monthComparison.abrPrognose - monthComparison.totals.abrVor, ((monthComparison.abrPrognose - monthComparison.totals.abrVor) / Math.abs(monthComparison.totals.abrVor)) * 100) : '–'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#1D4ED8' }}>
                    {formatEur(monthComparison.nettoPrognose)}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                    {formatEur(monthComparison.totals.nettoVor)}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: monthComparison.totals.nettoVor !== 0 ? (monthComparison.nettoPrognose >= monthComparison.totals.nettoVor ? '#059669' : '#DC2626') : '#9CA3AF' }}>
                    {monthComparison.totals.nettoVor !== 0 ? formatEurDelta(monthComparison.nettoPrognose - monthComparison.totals.nettoVor, ((monthComparison.nettoPrognose - monthComparison.totals.nettoVor) / Math.abs(monthComparison.totals.nettoVor)) * 100) : '–'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {monthComparison.hatEntwuerfe && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>
            Blau ausgewiesene Beträge stammen aus Rechnungsentwürfen und sind noch nicht fakturiert.
            Sie zählen deshalb nicht in die Gesamt-Zeile, sondern in die Prognose.
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* MITARBEITER-PERFORMANCE (Tim only, ab 2026)   */}
      {/* ============================================ */}
      {isTimWolter && selectedYear >= 2026 && (
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Section Header mit Toggle */}
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB',
            padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <UserCheck size={18} color="#0EA5E9" />
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                Mitarbeiter-Performance {zeitraumPrefix}{selectedYear}
              </span>
            </div>
            {staffPerformance.length > 0 && (
              <div style={{ display: 'flex', backgroundColor: '#F3F4F6', borderRadius: '8px', padding: '3px' }}>
                <button
                  onClick={() => setStaffMetric('umsatz')}
                  style={{
                    padding: '6px 14px', fontSize: '13px', fontWeight: '500', border: 'none', cursor: 'pointer',
                    borderRadius: '6px', transition: 'all 0.15s',
                    backgroundColor: staffMetric === 'umsatz' ? 'white' : 'transparent',
                    color: staffMetric === 'umsatz' ? '#3B82F6' : '#6B7280',
                    boxShadow: staffMetric === 'umsatz' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  Umsatz
                </button>
                <button
                  onClick={() => setStaffMetric('abrechnungen')}
                  style={{
                    padding: '6px 14px', fontSize: '13px', fontWeight: '500', border: 'none', cursor: 'pointer',
                    borderRadius: '6px', transition: 'all 0.15s',
                    backgroundColor: staffMetric === 'abrechnungen' ? 'white' : 'transparent',
                    color: staffMetric === 'abrechnungen' ? '#8B5CF6' : '#6B7280',
                    boxShadow: staffMetric === 'abrechnungen' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  Abrechnungen
                </button>
              </div>
            )}
          </div>

          {staffPerformance.length === 0 ? (
            /* Empty-State */
            <div style={{
              backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB',
              padding: '48px 20px', textAlign: 'center'
            }}>
              <Inbox size={40} color="#D1D5DB" style={{ margin: '0 auto 12px' }} />
              <div style={{ fontSize: '15px', fontWeight: '500', color: '#6B7280' }}>
                Noch keine Finalisierungen vorhanden
              </div>
              <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '6px' }}>
                Sobald Monate finalisiert werden, erscheint hier die Mitarbeiter-Performance.
              </div>
            </div>
          ) : (
            <>
              {/* Mitarbeiter-Tabelle */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB', width: '40px' }}>#</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Mitarbeiter</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Aktive Kunden</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Abrechnungen</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Umsatz</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Ø € / Abr.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffPerformance.map((row, i) => {
                        const isUnassigned = row.staffId === UNASSIGNED_STAFF_ID;
                        const barValue = staffMetric === 'umsatz' ? row.netto : row.abrechnungen;
                        const barColor = staffMetric === 'umsatz' ? '#3B82F6' : '#8B5CF6';
                        const barWidth = staffMaxBarValue > 0 ? Math.max((barValue / staffMaxBarValue) * 100, 0) : 0;
                        const avgPerAbr = row.abrechnungen > 0 ? row.netto / row.abrechnungen : 0;
                        return (
                          <tr key={row.staffId} style={{ borderBottom: '1px solid #F3F4F6', backgroundColor: isUnassigned ? '#FFFBEB' : 'white' }}>
                            <td style={{ padding: '12px 16px', color: '#6B7280', fontWeight: '500' }}>{i + 1}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{
                                  width: '32px', height: '32px', borderRadius: '50%',
                                  backgroundColor: isUnassigned ? '#FEF3C7' : '#F0F9FF',
                                  color: isUnassigned ? '#92400E' : '#0369A1',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '11px', fontWeight: '700', flexShrink: 0
                                }}>
                                  {row.initials}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', fontWeight: '500', color: isUnassigned ? '#92400E' : '#111827', marginBottom: '4px' }}>
                                    {row.full_name}
                                  </div>
                                  <div style={{ height: '4px', borderRadius: '2px', backgroundColor: '#F3F4F6', overflow: 'hidden' }}>
                                    <div style={{
                                      height: '100%', borderRadius: '2px', backgroundColor: barColor,
                                      width: `${barWidth}%`, transition: 'width 0.5s ease'
                                    }} />
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#111827' }}>{row.kunden || '–'}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                              {row.abrechnungen !== 0 ? formatNum(row.abrechnungen) : '–'}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                              {row.netto !== 0 ? formatEurExact(row.netto) : '–'}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: '#6B7280' }}>
                              {avgPerAbr > 0 ? formatEurExact(avgPerAbr) : '–'}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Summenzeile */}
                      <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600' }}>
                        <td style={{ padding: '12px 16px', color: '#111827' }} />
                        <td style={{ padding: '12px 16px', color: '#111827' }}>Gesamt</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#111827' }}>
                          {formatNum(staffPerformance.reduce((s, r) => s + r.kunden, 0))}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#111827' }}>
                          {formatNum(staffPerformance.reduce((s, r) => s + r.abrechnungen, 0))}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#111827' }}>
                          {formatEurExact(staffPerformance.reduce((s, r) => s + r.netto, 0))}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#6B7280' }}>
                          {(() => {
                            const totalAbr = staffPerformance.reduce((s, r) => s + r.abrechnungen, 0);
                            const totalNetto = staffPerformance.reduce((s, r) => s + r.netto, 0);
                            return totalAbr > 0 ? formatEurExact(totalNetto / totalAbr) : '–';
                          })()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Monats-Heatmap */}
              {staffMonthlyHeatmap.rows.length > 0 && (
                <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                      Monatlicher Verlauf – {staffMetric === 'umsatz' ? 'Umsatz' : 'Abrechnungen'} pro Mitarbeiter
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#9CA3AF' }}>
                      <span>wenig</span>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        {[0.1, 0.25, 0.45, 0.7, 0.95].map(o => (
                          <div key={o} style={{
                            width: '14px', height: '14px', borderRadius: '3px',
                            backgroundColor: staffMetric === 'umsatz' ? `rgba(59, 130, 246, ${o})` : `rgba(139, 92, 246, ${o})`
                          }} />
                        ))}
                      </div>
                      <span>viel</span>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB', position: 'sticky', left: 0, backgroundColor: '#F9FAFB', minWidth: '180px' }}>
                            Mitarbeiter
                          </th>
                          {monatsSpalten.map(m => (
                            <th key={m} style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB', minWidth: '60px' }}>
                              {GERMAN_MONTHS[m]}
                            </th>
                          ))}
                          <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB', minWidth: '110px' }}>
                            Σ {istGanzesJahr ? selectedYear : zeitraumKurz}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {staffMonthlyHeatmap.rows.map(row => {
                          const isUnassigned = row.staffId === UNASSIGNED_STAFF_ID;
                          const rowSum = row.cells.reduce((s, c) => s + (staffMetric === 'umsatz' ? c.netto : c.abrechnungen), 0);
                          return (
                            <tr key={row.staffId} style={{ borderBottom: '1px solid #F3F4F6' }}>
                              <td style={{
                                padding: '8px 16px', position: 'sticky', left: 0,
                                backgroundColor: isUnassigned ? '#FFFBEB' : 'white',
                                borderRight: '1px solid #F3F4F6'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span style={{
                                    width: '24px', height: '24px', borderRadius: '50%',
                                    backgroundColor: isUnassigned ? '#FEF3C7' : '#F0F9FF',
                                    color: isUnassigned ? '#92400E' : '#0369A1',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '10px', fontWeight: '700', flexShrink: 0
                                  }}>
                                    {row.initials}
                                  </span>
                                  <span style={{ fontSize: '12px', fontWeight: '500', color: isUnassigned ? '#92400E' : '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {row.full_name}
                                  </span>
                                </div>
                              </td>
                              {row.cells.map(cell => {
                                const value = staffMetric === 'umsatz' ? cell.netto : cell.abrechnungen;
                                const intensity = staffMonthlyHeatmap.maxValue > 0 ? value / staffMonthlyHeatmap.maxValue : 0;
                                const opacity = value > 0 ? Math.max(intensity * 0.85 + 0.1, 0.1) : 0;
                                const bgColor = staffMetric === 'umsatz'
                                  ? `rgba(59, 130, 246, ${opacity})`
                                  : `rgba(139, 92, 246, ${opacity})`;
                                const textColor = opacity > 0.55 ? 'white' : (value > 0 ? '#111827' : '#D1D5DB');
                                const displayValue = value === 0
                                  ? '–'
                                  : staffMetric === 'umsatz'
                                    ? (value >= 1000 ? `${(value / 1000).toFixed(0)}k` : formatNum(Math.round(value)))
                                    : formatNum(Math.round(value));
                                const tooltip = value > 0
                                  ? `${row.full_name} – ${GERMAN_MONTHS_FULL[cell.month]} ${selectedYear}\nUmsatz: ${formatEurExact(cell.netto)}\nAbrechnungen: ${formatNum(cell.abrechnungen)}`
                                  : `${row.full_name} – ${GERMAN_MONTHS_FULL[cell.month]} ${selectedYear}: keine Aktivität`;
                                return (
                                  <td key={cell.month} style={{ padding: '4px', textAlign: 'center' }}>
                                    <div
                                      title={tooltip}
                                      style={{
                                        backgroundColor: bgColor,
                                        color: textColor,
                                        padding: '8px 4px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        minHeight: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      {displayValue}
                                    </div>
                                  </td>
                                );
                              })}
                              <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '600', color: '#111827', backgroundColor: '#F9FAFB' }}>
                                {staffMetric === 'umsatz'
                                  ? (rowSum > 0 ? formatEur(rowSum) : '–')
                                  : (rowSum > 0 ? formatNum(rowSum) : '–')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* UNIT ECONOMICS (Tim only, ab 2026)            */}
      {/* ============================================ */}
      {isTimWolter && selectedYear >= 2026 && unitEconomics && (
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Section Header */}
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB',
            padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Calculator size={18} color="#0D9488" />
              <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                Unit Economics {zeitraumPrefix}{selectedYear}
              </span>
            </div>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>
              {unitEconomics.hatKostenBasis
                ? `Basis: ${formatMonatsliste(unitEconomics.vollstaendigeMonate)}`
                : 'Keine vollständig erfassten Kosten'}
            </span>
          </div>

          {/* Lückenhinweis Personalkosten */}
          {unitEconomics.offeneMonatePersonal.length > 0 && (
            <div style={{
              backgroundColor: '#FFFBEB', borderRadius: '12px', border: '1px solid #FDE68A',
              padding: '14px 20px', display: 'flex', alignItems: 'flex-start', gap: '10px'
            }}>
              <AlertTriangle size={18} color="#B45309" style={{ flexShrink: 0, marginTop: '1px' }} />
              <div style={{ fontSize: '13px', color: '#92400E', lineHeight: '1.5' }}>
                Für {formatMonatsliste(unitEconomics.offeneMonatePersonal)} liegen keine vollständig erfassten
                Personalkosten vor. Diese Monate fließen nicht in die Berechnung ein.
              </div>
            </div>
          )}

          {/* Lückenhinweis DATEV-Rechnung */}
          {unitEconomics.offeneMonateDatev.length > 0 && (
            <div style={{
              backgroundColor: '#FFFBEB', borderRadius: '12px', border: '1px solid #FDE68A',
              padding: '14px 20px', display: 'flex', alignItems: 'flex-start', gap: '10px'
            }}>
              <AlertTriangle size={18} color="#B45309" style={{ flexShrink: 0, marginTop: '1px' }} />
              <div style={{ fontSize: '13px', color: '#92400E', lineHeight: '1.5' }}>
                Für {formatMonatsliste(unitEconomics.offeneMonateDatev)} ist keine DATEV-Rechnung erfasst.
                Diese Monate fließen nicht in die Berechnung ein. Erfasst wird die Rechnung über die DATEV-Weiterberechnung.
              </div>
            </div>
          )}

          {!unitEconomics.hatKostenBasis ? (
            /* Ohne Kostenbasis: nur Umsatz und DATEV */
            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                      <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Position</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Betrag</th>
                      <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>je Abrechnung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitEconomics.fensterDatevMonate.length > 0 ? (
                      <>
                        <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '10px 16px', color: '#111827' }}>Umsatz</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>{formatEurExact(unitEconomics.umsatzFensterDatev)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                            {unitEconomics.abrFensterDatev > 0 ? formatEurExact(unitEconomics.umsatzFensterDatev / unitEconomics.abrFensterDatev) : '–'}
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '10px 16px', color: '#111827' }}>DATEV gesamt</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626', fontWeight: '500' }}>
                            {formatEurExact(unitEconomics.datevFenster)}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                            {unitEconomics.abrFensterDatev > 0 ? formatEurExact(unitEconomics.datevFenster / unitEconomics.abrFensterDatev) : '–'}
                          </td>
                        </tr>
                        <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600' }}>
                          <td style={{ padding: '10px 16px', color: '#111827' }}>Deckungsbeitrag I</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>
                            {formatEurExact(unitEconomics.umsatzFensterDatev - unitEconomics.datevFenster)}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                            {unitEconomics.abrFensterDatev > 0
                              ? formatEurExact((unitEconomics.umsatzFensterDatev - unitEconomics.datevFenster) / unitEconomics.abrFensterDatev)
                              : '–'}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} style={{ padding: '12px 16px', fontSize: '12px', color: '#9CA3AF' }}>
                            Basis: {formatMonatsliste(unitEconomics.fensterDatevMonate)}. Die Personalstufen und die
                            Schwellenwerte je Abrechnung erscheinen, sobald für mindestens einen Monat im gewählten
                            Zeitraum alle Personalkosten und die DATEV-Rechnung erfasst sind.
                          </td>
                        </tr>
                      </>
                    ) : (
                      <>
                        <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={{ padding: '10px 16px', color: '#111827' }}>Umsatz</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>{formatEurExact(unitEconomics.umsatzFenster)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                            {unitEconomics.abrFenster > 0 ? formatEurExact(unitEconomics.umsatzFenster / unitEconomics.abrFenster) : '–'}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} style={{ padding: '12px 16px', fontSize: '12px', color: '#9CA3AF' }}>
                            Für den gewählten Zeitraum ist noch keine DATEV-Rechnung erfasst. DATEV-Kosten,
                            Deckungsbeiträge und Schwellenwerte erscheinen, sobald die Rechnung über die
                            DATEV-Weiterberechnung erfasst ist.
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              {/* Deckungsbeitragsstufen */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Position</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Betrag</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>je Abrechnung</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Anteil Umsatz</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>
                          Umsatz
                          <span style={{ color: '#9CA3AF', marginLeft: '8px' }}>{formatNum(unitEconomics.abrBasis)} Abrechnungen</span>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>{formatEurExact(unitEconomics.umsatzBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>
                          {formatEurExact(unitEconomics.umsatzBasis / unitEconomics.abrBasis)}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>100,0%</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>DATEV mandantenbezogen</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626', fontWeight: '500' }}>{formatEurExact(unitEconomics.datevBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.datevBasis / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatQuote(unitEconomics.datevBasis, unitEconomics.umsatzBasis)}</td>
                      </tr>
                      <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600', borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>Deckungsbeitrag I</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{formatEurExact(unitEconomics.dbI)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#374151' }}>{formatEurExact(unitEconomics.dbI / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#374151' }}>{formatQuote(unitEconomics.dbI, unitEconomics.umsatzBasis)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>Personal, abrechnend</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626', fontWeight: '500' }}>{formatEurExact(unitEconomics.personalAbrechnend)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.personalAbrechnend / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatQuote(unitEconomics.personalAbrechnend, unitEconomics.umsatzBasis)}</td>
                      </tr>
                      <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600', borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>Deckungsbeitrag II</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#111827' }}>{formatEurExact(unitEconomics.dbII)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#374151' }}>{formatEurExact(unitEconomics.dbII / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#374151' }}>{formatQuote(unitEconomics.dbII, unitEconomics.umsatzBasis)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>Support Staff</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626', fontWeight: '500' }}>{formatEurExact(unitEconomics.personalSupport)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.personalSupport / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatQuote(unitEconomics.personalSupport, unitEconomics.umsatzBasis)}</td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '10px 16px', color: '#111827' }}>
                          DATEV allgemein
                          {unitEconomics.datevOhneZuordnung !== 0 && (
                            <span style={{ color: '#9CA3AF', marginLeft: '8px' }}>
                              davon {formatEurExact(unitEconomics.datevOhneZuordnung)} ohne zuordenbaren Kunden
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#DC2626', fontWeight: '500' }}>{formatEurExact(unitEconomics.datevAllgemein)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.datevAllgemein / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: '#6B7280' }}>{formatQuote(unitEconomics.datevAllgemein, unitEconomics.umsatzBasis)}</td>
                      </tr>
                      <tr style={{ backgroundColor: '#ECFDF5', fontWeight: '700' }}>
                        <td style={{ padding: '12px 16px', color: '#065F46' }}>Deckungsbeitrag III</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#065F46' }}>{formatEurExact(unitEconomics.dbIII)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#047857' }}>{formatEurExact(unitEconomics.dbIII / unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: '#047857' }}>{formatQuote(unitEconomics.dbIII, unitEconomics.umsatzBasis)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', display: 'flex', flexWrap: 'wrap', gap: '24px', backgroundColor: '#FAFBFC' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Produktionskosten je Abrechnung</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{formatEurExact(unitEconomics.produktionskostenJeAbr)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Vollkosten je Abrechnung</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{formatEurExact(unitEconomics.vollkostenJeAbr)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>Support-Staff-Umlage je Kopf</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                      {formatEurExact(unitEconomics.overheadJeKopf)}
                      <span style={{ fontSize: '11px', fontWeight: '400', color: '#9CA3AF', marginLeft: '6px' }}>
                        auf {unitEconomics.anzahlAbrechnendeMitKosten} Köpfe
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '2px' }}>DATEV-allgemein-Umlage je Kopf</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>
                      {formatEurExact(unitEconomics.datevAllgemeinJeKopf)}
                      <span style={{ fontSize: '11px', fontWeight: '400', color: '#9CA3AF', marginLeft: '6px' }}>
                        auf {unitEconomics.anzahlAbrechnendeMitKosten} Köpfe
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Je Mitarbeiter */}
              <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                    Deckungsbeitrag je Mitarbeiter
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F9FAFB' }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Mitarbeiter</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Abr.</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Umsatz</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Ø € / Abr.</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>DATEV</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Personal</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Support-Anteil</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>DATEV allg.</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Deckungsbeitrag</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Break-even</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>Auslastung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unitEconomics.zeilen.map(row => {
                        const preisJeAbr = row.abrechnungen > 0 ? row.netto / row.abrechnungen : 0;
                        const datev = row.datev;
                        const datevJeAbr = row.abrechnungen > 0 ? datev / row.abrechnungen : 0;
                        const eigeneKosten = row.personalkosten !== null ? row.personalkosten : 0;
                        const db = row.netto - datev - eigeneKosten - row.overheadAnteil - row.datevAllgemeinAnteil;
                        const dbJeAbr = preisJeAbr - datevJeAbr;
                        const kostenBlock = eigeneKosten + row.overheadAnteil + row.datevAllgemeinAnteil;
                        const breakEven = (row.personalkosten !== null && dbJeAbr > 0)
                          ? Math.ceil(kostenBlock / dbJeAbr)
                          : null;
                        const auslastung = (breakEven !== null && breakEven > 0)
                          ? (row.abrechnungen / breakEven) * 100
                          : null;
                        return (
                          <tr key={row.staffId} style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{
                                  width: '28px', height: '28px', borderRadius: '50%',
                                  backgroundColor: row.istSupportStaff ? '#F5F3FF' : '#F0FDFA',
                                  color: row.istSupportStaff ? '#6D28D9' : '#0F766E',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '10px', fontWeight: '700', flexShrink: 0
                                }}>
                                  {row.initials}
                                </span>
                                <div>
                                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#111827' }}>{row.full_name}</div>
                                  {row.istSupportStaff && (
                                    <div style={{ fontSize: '11px', color: '#6D28D9' }}>Support Staff</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>
                              {row.abrechnungen !== 0 ? formatNum(row.abrechnungen) : '–'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#111827' }}>
                              {row.netto !== 0 ? formatEurExact(row.netto) : '–'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                              {preisJeAbr > 0 ? formatEurExact(preisJeAbr) : '–'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                              {datev !== 0 ? formatEurExact(datev) : '–'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                              {row.personalkosten !== null ? formatEurExact(row.personalkosten) : '–'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                              {row.overheadAnteil !== 0 ? formatEurExact(row.overheadAnteil) : '–'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                              {row.datevAllgemeinAnteil !== 0 ? formatEurExact(row.datevAllgemeinAnteil) : '–'}
                            </td>
                            <td style={{
                              padding: '12px', textAlign: 'right', fontWeight: '600',
                              color: db >= 0 ? '#059669' : '#DC2626'
                            }}>
                              {formatEurExact(db)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                              {breakEven !== null ? formatNum(breakEven) : '–'}
                            </td>
                            <td style={{
                              padding: '12px 16px', textAlign: 'right', fontWeight: '500',
                              color: auslastung === null ? '#9CA3AF' : (auslastung >= 100 ? '#059669' : '#DC2626')
                            }}>
                              {auslastung !== null ? `${auslastung.toFixed(0)}%` : '–'}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Summenzeile */}
                      <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600' }}>
                        <td style={{ padding: '12px 16px', color: '#111827' }}>Gesamt</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#111827' }}>{formatNum(unitEconomics.abrBasis)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#111827' }}>{formatEurExact(unitEconomics.umsatzBasis)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>
                          {formatEurExact(unitEconomics.umsatzBasis / unitEconomics.abrBasis)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.datevBasis)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.personalAbrechnend)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.personalSupport)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#6B7280' }}>{formatEurExact(unitEconomics.datevAllgemein)}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: unitEconomics.dbIII >= 0 ? '#059669' : '#DC2626' }}>
                          {formatEurExact(unitEconomics.dbIII)}
                        </td>
                        <td style={{ padding: '12px' }} />
                        <td style={{ padding: '12px 16px' }} />
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '12px 16px', borderTop: '1px solid #E5E7EB', fontSize: '12px', color: '#9CA3AF', lineHeight: '1.5' }}>
                  Break-even und Auslastung beziehen sich auf den jeweils eigenen Durchschnittspreis je Abrechnung.
                  Die Kosten des Support Staff sind nicht in dessen eigener Zeile ausgewiesen, sondern gleichmäßig
                  als Support-Anteil auf die abrechnenden Mitarbeiter verteilt. DATEV enthält die Kosten der
                  betreuten Mandanten laut DATEV-Rechnung inklusive anteiligem Abrechnungsbonus. DATEV allgemein
                  wird wie der Support Staff gleichmäßig verteilt.
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
